import { Component, OnInit, Input, inject, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridReadyEvent, ICellRendererParams, PaginationChangedEvent } from 'ag-grid-community';
import { AG_GRID_PAGE_SIZE_OPTIONS, AG_GRID_DEFAULT_PAGE_SIZE } from '../../shared/ag-grid/ag-grid-pagination.util';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { InstallerLogService } from '../../services/installer-log/installer-log.service';
import { InstallerLogViewDialogComponent } from '../installer-log-view-dialog/installer-log-view-dialog.component';
import { TextLogViewerComponent } from '../text-log-viewer/text-log-viewer.component';
import { ToastrService } from 'ngx-toastr';
import moment from 'moment';
import { syncInfiniteRowModelPageSize } from '../../shared/ag-grid/ag-grid-pagination.util';
import { AgGridCopyCellTooltipDirective } from '../../shared/ag-grid/ag-grid-copy-cell-tooltip.directive';

@Component({
  selector: 'app-installer-log-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AgGridAngular,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    AgGridCopyCellTooltipDirective,
  ],
  templateUrl: './installer-log-list.component.html',
  styleUrl: './installer-log-list.component.scss',
})
export class InstallerLogListComponent implements OnInit {
  private installerLogService = inject(InstallerLogService);
  private toastr = inject(ToastrService);
  private dialog = inject(MatDialog);

  @Input() embeddedGatewayId?: string;

  gatewayIdFilter = '';
  fileNameFilter = '';

  gridApi!: GridApi;
  paginationPageSize = AG_GRID_DEFAULT_PAGE_SIZE;
  readonly pageSizeOptions = AG_GRID_PAGE_SIZE_OPTIONS;
  isLoading = false;
  isContentLoading = false;

  contextMenuVisible = false;
  contextMenuPosition = { x: 0, y: 0 };
  selectedCellValue = '';

  defaultColDef: ColDef = {
    sortable: true,
    sortingOrder: ['asc', 'desc'],
    floatingFilter: false,
    resizable: true,
  };

  formatUtcDate(value: any): string {
    if (!value) return 'N/A';
    return moment.utc(value).format('YYYY-MM-DD HH:mm:ss');
  }

  formatLogDate(row: { logDate?: string } | undefined): string {
    return this.formatUtcDate(row?.logDate);
  }

  columnDefs: ColDef[] = [
    { headerName: 'File Name', field: 'fileName', flex: 2 },
    {
      headerName: 'Uploaded At (UTC)',
      field: 'logDate',
      sortable: true,
      sort: 'desc',
      sortIndex: 0,
      width: 200,
      valueGetter: (params) => this.formatLogDate(params?.data),
      flex: 2
    },
    {
      headerName: 'View',
      field: 'actions',
      width: 80,
      sortable: false,
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.data) return '';
        const btn = document.createElement('button');
        btn.className = 'view-icon-btn';
        btn.title = this.getViewTooltip(params.data?.fileName);
        const icon = document.createElement('span');
        icon.className = 'material-icons';
        icon.textContent = 'visibility';
        btn.appendChild(icon);
        btn.addEventListener('click', () => this.openViewer(params.data));
        return btn;
      },
      flex: 1
    },
    {
      headerName: 'Download',
      field: 'download',
      width: 100,
      sortable: false,
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.data) return '';
        const btn = document.createElement('button');
        btn.className = 'view-icon-btn';
        btn.type = 'button';
        btn.title = 'Download';
        const icon = document.createElement('span');
        icon.className = 'material-icons';
        icon.textContent = 'download';
        btn.appendChild(icon);
        btn.addEventListener('click', (event) => {
          event.stopPropagation();
          this.downloadLog(params.data);
        });
        return btn;
      },
      flex: 1
    },
  ];

  searchPayload: any = {
    page: 0,
    size: this.paginationPageSize,
    filters: [],
    sorts: [{ field: 'logDate', direction: 'desc' }],
  };

  ngOnInit(): void {
    if (this.embeddedGatewayId) {
      this.gatewayIdFilter = this.embeddedGatewayId;
    }
  }

  onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api;
    this.gridApi.setGridOption('datasource', this.dataSource);
  }

  onPaginationChanged(event: PaginationChangedEvent): void {
    syncInfiniteRowModelPageSize(
      event,
      this.paginationPageSize,
      (newPageSize) => {
        this.paginationPageSize = newPageSize;
        this.searchPayload.size = newPageSize;
        this.searchPayload.page = 0;
      }
    );
    this.searchPayload.page = event.api.paginationGetCurrentPage();
  }

  buildFilters() {
    const filters: any[] = [];
    if (this.gatewayIdFilter.trim()) {
      filters.push({ field: 'gatewayId', operator: 'like', value: this.gatewayIdFilter.trim() });
    }
    if (this.fileNameFilter.trim()) {
      filters.push({ field: 'fileName', operator: 'like', value: this.fileNameFilter.trim() });
    }
    this.searchPayload.filters = filters;
  }

  sortForAgGrid(sortModel: any) {
    if (!sortModel?.[0]?.colId) return;
    this.searchPayload.sorts[0] = { field: sortModel[0].colId, direction: sortModel[0].sort };
  }

  dataSource = {
    getRows: (params: any) => {
      this.sortForAgGrid(params.sortModel);
      this.buildFilters();
      if (params.startRow === 0) this.searchPayload.page = 0;
      this.isLoading = true;
      this.installerLogService.searchLogs({ ...this.searchPayload }).subscribe({
        next: (data) => {
          const res = data?.data || data;
          params.successCallback(res.content || [], res.totalElements || 0);
          this.isLoading = false;
        },
        error: () => {
          params.failCallback();
          this.isLoading = false;
          this.toastr.error('Failed to load installer logs');
        },
      });
    },
  };

  onFilterChange() {
    this.searchPayload.page = 0;
    if (this.gridApi) this.gridApi.setGridOption('datasource', this.dataSource);
  }

  openViewer(row: any) {
    const extension = this.getFileExtension(row?.fileName);
    if (extension === 'txt') {
      this.openTextViewer(row);
      return;
    }

    this.isContentLoading = true;
    this.installerLogService.getLogContent(row.id).subscribe({
      next: (res) => {
        this.isContentLoading = false;
        this.dialog.open(InstallerLogViewDialogComponent, {
          width: '92vw',
          maxWidth: '1500px',
          height: '88vh',
          panelClass: 'csv-viewer-dialog',
          data: { content: res?.data ?? res ?? '', fileName: row.fileName },
        });
      },
      error: () => {
        this.isContentLoading = false;
        this.toastr.error('Failed to load log content');
      },
    });
  }

  private openTextViewer(row: any): void {
    this.isContentLoading = true;
    this.installerLogService.getLogContentAsText(row.id).subscribe({
      next: (content) => {
        this.isContentLoading = false;
        const normalizedText = this.normalizeTextContent(content);
        this.dialog.open(TextLogViewerComponent, {
          width: '80vw',
          maxWidth: '95vw',
          height: '85vh',
          panelClass: 'text-log-viewer-dialog',
          data: { content: normalizedText, fileName: row.fileName },
        });
      },
      error: () => {
        this.isContentLoading = false;
        this.toastr.error('Failed to load text log content');
      },
    });
  }

  /**
   * Downloads the installer log for this row.
   * Uses the same content API path as View for each file type so CSV/TXT
   * downloads match what the viewer displays (exact file content).
   */
  downloadLog(row: { id?: number; fileName?: string } | null | undefined): void {
    const logId = Number(row?.id);
    const fileName = String(row?.fileName ?? '').trim();
    if (!Number.isFinite(logId) || logId <= 0 || !fileName) {
      this.toastr.error('Unable to download installer log');
      return;
    }

    this.isContentLoading = true;
    const extension = this.getFileExtension(fileName);

    // Match View: TXT uses text response + envelope unwrap; CSV uses JSON `data`.
    if (extension === 'txt') {
      this.installerLogService.getLogContentAsText(logId).subscribe({
        next: (rawContent) => {
          this.isContentLoading = false;
          this.triggerBrowserDownload(
            this.normalizeTextContent(rawContent),
            fileName
          );
        },
        error: () => {
          this.isContentLoading = false;
          this.toastr.error('Failed to download installer log');
        },
      });
      return;
    }

    this.installerLogService.getLogContent(logId).subscribe({
      next: (res) => {
        this.isContentLoading = false;
        const content = res?.data ?? res ?? '';
        this.triggerBrowserDownload(
          typeof content === 'string' ? content : String(content ?? ''),
          fileName
        );
      },
      error: () => {
        this.isContentLoading = false;
        this.toastr.error('Failed to download installer log');
      },
    });
  }

  private triggerBrowserDownload(content: string, fileName: string): void {
    const extension = this.getFileExtension(fileName);
    const mimeType =
      extension === 'csv'
        ? 'text/csv;charset=utf-8'
        : extension === 'txt'
          ? 'text/plain;charset=utf-8'
          : 'application/octet-stream';

    const blob = new Blob([content ?? ''], { type: mimeType });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  }

  private getFileExtension(fileName: string | undefined | null): string {
    const normalized = String(fileName ?? '').trim().toLowerCase();
    const lastDot = normalized.lastIndexOf('.');
    if (lastDot < 0 || lastDot === normalized.length - 1) {
      return '';
    }
    return normalized.slice(lastDot + 1);
  }

  private getViewTooltip(fileName: string | undefined | null): string {
    const extension = this.getFileExtension(fileName);
    if (extension === 'txt') {
      return 'View plain text';
    }
    return 'View CSV data';
  }

  /**
   * Some backends return TXT content inside a JSON envelope string:
   * {"status":200,"data":"line1\nline2"}.
   * For TXT viewer we need only the raw text body.
   */
  private normalizeTextContent(rawContent: string): string {
    const content = String(rawContent ?? '');
    const trimmed = content.trim();

    if (!(trimmed.startsWith('{') && trimmed.endsWith('}'))) {
      return content;
    }

    try {
      const parsed = JSON.parse(trimmed) as { data?: unknown };
      if (typeof parsed?.data === 'string') {
        return this.decodeTransportEscapes(parsed.data);
      }
      return content;
    } catch {
      return content;
    }
  }

  private decodeTransportEscapes(text: string): string {
    return String(text ?? '')
      .replace(/\\r\\n/g, '\n')
      .replace(/\\n/g, '\n')
      .replace(/\\r/g, '\n')
      .replace(/\\t/g, '\t');
  }

  onCellContextMenu(event: any) {
    event.event.preventDefault();
    if (event.node?.data) {
      this.selectedCellValue = String(event.value ?? '');
      this.contextMenuPosition = { x: event.event.clientX, y: event.event.clientY };
      this.contextMenuVisible = true;
    }
  }

  handleCopy() {
    navigator.clipboard.writeText(this.selectedCellValue).then(() => {
      this.contextMenuVisible = false;
    });
  }

  @HostListener('document:click')
  onDocumentClick() { this.contextMenuVisible = false; }

  preventDefaultContextMenu(event: MouseEvent) { event.preventDefault(); }
}
